import React, { useState, useMemo, useCallback } from 'react';
import { theme } from '../../theme';
import { useResponsive } from '../../hooks/useResponsive';
import { useInvitableUsers, InvitableUser, GroupPendingMember } from '../../hooks/useInvitableUsers';
import { track } from '../../lib/analytics';

interface InvitePlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  matchId: string;
  matchTitle?: string;
  isOrganizer: boolean;
  isPlayerInMatch: boolean;
  matchStatus?: string;
  onInviteSuccess: () => void;
  onShowNotification: (message: string, type: 'success' | 'error' | 'warning' | 'info') => void;
}

type TabId = 'friends' | 'share' | 'guest';

const POSITIONS = ['Arquero', 'Defensor', 'Mediocampista', 'Delantero'];

export const InvitePlayerModal: React.FC<InvitePlayerModalProps> = ({
  isOpen,
  onClose,
  matchId,
  matchTitle = 'Partido',
  isOrganizer,
  isPlayerInMatch,
  matchStatus,
  onInviteSuccess,
  onShowNotification,
}) => {
  const { isMobile } = useResponsive();
  const { data, loading, error, sendInvitations, addGuest, proposeGuest, refetch } =
    useInvitableUsers(matchId, isOpen);

  const [activeTab, setActiveTab] = useState<TabId>('friends');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  // Guest form state
  const [guestName, setGuestName] = useState('');
  const [guestPosition, setGuestPosition] = useState('');
  const [guestRating, setGuestRating] = useState<number>(0);
  const [addingGuest, setAddingGuest] = useState(false);

  // Smart default tab: if no groups/friends, default to share
  const defaultTab = useMemo(() => {
    if (!data) return 'friends';
    const hasUsers = data.groups.some(g => g.users.length > 0) || data.friends.length > 0;
    return hasUsers ? 'friends' : 'share';
  }, [data]);

  // Set default tab when data loads
  React.useEffect(() => {
    if (data && activeTab === 'friends') {
      const hasUsers = data.groups.some(g => g.users.length > 0) || data.friends.length > 0;
      if (!hasUsers) setActiveTab('share');
    }
  }, [data, activeTab]);

  // Flatten all users for the friends tab, deduplicated
  const allUsers = useMemo(() => {
    if (!data) return [];
    const seen = new Set<string>();
    const users: InvitableUser[] = [];

    // Groups first
    for (const group of data.groups) {
      for (const user of group.users) {
        if (!seen.has(user.id)) {
          seen.add(user.id);
          users.push(user);
        }
      }
    }
    // Then friends
    for (const friend of data.friends) {
      if (!seen.has(friend.id)) {
        seen.add(friend.id);
        users.push(friend);
      }
    }
    return users;
  }, [data]);

  // Filter by search
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return allUsers;
    const q = searchQuery.toLowerCase();
    return allUsers.filter(
      u =>
        u.name.toLowerCase().includes(q) ||
        u.nickname?.toLowerCase().includes(q) ||
        u.groupName?.toLowerCase().includes(q)
    );
  }, [allUsers, searchQuery]);

  const toggleUser = useCallback((userId: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }, []);

  const handleSendInvitations = async () => {
    const ids = Array.from(selectedUserIds);
    if (ids.length === 0) return;
    setSending(true);
    const ok = await sendInvitations(ids);
    setSending(false);
    if (ok) {
      track('invite_friend_sent', { match_id: matchId, count: ids.length, platform: 'web' });
      onShowNotification(`${ids.length} invitacion${ids.length > 1 ? 'es' : ''} enviada${ids.length > 1 ? 's' : ''}`, 'success');
      setSelectedUserIds(new Set());
      refetch();
      onInviteSuccess();
    } else {
      onShowNotification('Error al enviar invitaciones', 'error');
    }
  };

  // Cache-buster por share: WhatsApp/Telegram cachean previews por URL, así que
  // agregamos un timestamp para que cada link compartido genere preview fresco.
  const shareUrl = `${process.env.REACT_APP_CLIENT_URL || window.location.origin}/join/${matchId}?t=${Date.now()}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    track('invite_link_shared', { match_id: matchId, channel: 'copy', platform: 'web' });
    setCopied(true);
    onShowNotification('Link copiado al portapapeles', 'success');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareWhatsApp = () => {
    const message = `Unite al partido: ${matchTitle}\n\n${shareUrl}`;
    track('invite_link_shared', { match_id: matchId, channel: 'whatsapp', platform: 'web' });
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleShareTelegram = () => {
    const message = `Unite al partido: ${matchTitle}\n\n${shareUrl}`;
    track('invite_link_shared', { match_id: matchId, channel: 'telegram', platform: 'web' });
    window.open(
      `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(message)}`,
      '_blank'
    );
  };

  const handleAddGuest = async () => {
    if (!guestName.trim()) return;
    setAddingGuest(true);
    const fn = isOrganizer ? addGuest : proposeGuest;
    const ok = await fn(guestName, guestPosition || undefined, guestRating || undefined);
    setAddingGuest(false);
    if (ok) {
      track('guest_added', { match_id: matchId, is_organizer: isOrganizer, platform: 'web' });
      const msg = isOrganizer
        ? `${guestName} agregado al partido`
        : `Invitado "${guestName}" propuesto al organizador`;
      onShowNotification(msg, 'success');
      setGuestName('');
      setGuestPosition('');
      setGuestRating(0);
      onInviteSuccess();
    } else {
      onShowNotification('Error al agregar invitado', 'error');
    }
  };

  const handleAddPendingMember = async (pm: GroupPendingMember) => {
    const ok = await addGuest(pm.guestName, pm.guestPosition, pm.guestRating, pm.id);
    if (ok) {
      track('pending_member_added_to_match', { match_id: matchId, pending_member_id: pm.id, platform: 'web' });
      onShowNotification(`${pm.guestName} agregado al partido`, 'success');
      refetch();
      onInviteSuccess();
    } else {
      onShowNotification('Error al agregar jugador', 'error');
    }
  };

  // Flatten pending members from all groups (only shown to organizer)
  const groupPendingMembers: GroupPendingMember[] = isOrganizer
    ? (data?.groups ?? []).flatMap(g => g.pendingMembers ?? [])
    : [];

  if (!isOpen) return null;

  const isScheduled = matchStatus === 'SCHEDULED';
  const tabs: { id: TabId; label: string; icon: string }[] = [
    { id: 'friends', label: 'Amigos', icon: '👥' },
    { id: 'share', label: 'Compartir', icon: '🔗' },
    ...(isScheduled
      ? [{ id: 'guest' as TabId, label: 'Invitado', icon: '➕' }]
      : []),
  ];

  // Non-organizer, non-participant: share only
  const shareOnly = !isPlayerInMatch && !isOrganizer;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: isMobile ? '12px' : '40px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          backgroundColor: theme.colors.surface,
          borderRadius: theme.borderRadius.card,
          maxWidth: '600px',
          width: '100%',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          border: `2px solid ${theme.colors.primary}`,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: `${theme.spacing.md} ${theme.spacing.md}`,
            borderBottom: `1px solid ${theme.colors.border}`,
            flexShrink: 0,
          }}
        >
          <h2
            style={{
              color: theme.colors.primary,
              fontFamily: theme.fonts.heading,
              fontSize: '1.4rem',
              margin: 0,
              letterSpacing: theme.fonts.letterSpacingWide,
            }}
          >
            Invitar al partido
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: theme.colors.textSecondary,
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        {!shareOnly && (
          <div
            style={{
              display: 'flex',
              borderBottom: `1px solid ${theme.colors.border}`,
              flexShrink: 0,
            }}
          >
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: 1,
                  padding: `${theme.spacing.sm} ${theme.spacing.xs}`,
                  background: 'none',
                  border: 'none',
                  borderBottom: activeTab === tab.id ? `3px solid ${theme.colors.primary}` : '3px solid transparent',
                  color: activeTab === tab.id ? theme.colors.primary : theme.colors.textSecondary,
                  fontFamily: theme.fonts.body,
                  fontSize: theme.fonts.sizeSmall,
                  fontWeight: activeTab === tab.id ? 700 : 400,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: theme.spacing.md }}>
          {(activeTab === 'friends' && !shareOnly) && (
            <FriendsTab
              users={filteredUsers}
              loading={loading}
              error={error}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              selectedUserIds={selectedUserIds}
              onToggleUser={toggleUser}
              hasGroups={!!data && data.groups.length > 0}
            />
          )}
          {(activeTab === 'share' || shareOnly) && (
            <ShareTab
              shareUrl={shareUrl}
              matchTitle={matchTitle}
              copied={copied}
              onCopyLink={handleCopyLink}
              onShareWhatsApp={handleShareWhatsApp}
              onShareTelegram={handleShareTelegram}
              isMobile={isMobile}
            />
          )}
          {activeTab === 'guest' && !shareOnly && isScheduled && (
            <GuestTab
              isOrganizer={isOrganizer}
              guestName={guestName}
              guestPosition={guestPosition}
              guestRating={guestRating}
              onNameChange={setGuestName}
              onPositionChange={setGuestPosition}
              onRatingChange={setGuestRating}
              onSubmit={handleAddGuest}
              adding={addingGuest}
              pendingMembers={groupPendingMembers}
              onAddPendingMember={handleAddPendingMember}
            />
          )}
        </div>

        {/* Footer: Send invitations button (only on friends tab) */}
        {activeTab === 'friends' && !shareOnly && selectedUserIds.size > 0 && (
          <div
            style={{
              padding: theme.spacing.md,
              borderTop: `1px solid ${theme.colors.border}`,
              flexShrink: 0,
            }}
          >
            <button
              onClick={handleSendInvitations}
              disabled={sending}
              style={{
                width: '100%',
                padding: theme.spacing.sm,
                backgroundColor: sending ? theme.colors.secondary : theme.colors.primary,
                color: sending ? theme.colors.textSecondary : '#000',
                border: 'none',
                borderRadius: theme.borderRadius.button,
                fontFamily: theme.fonts.body,
                fontSize: theme.fonts.sizeBody,
                fontWeight: 700,
                cursor: sending ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              {sending
                ? 'Enviando...'
                : `Enviar ${selectedUserIds.size} invitacion${selectedUserIds.size > 1 ? 'es' : ''}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Friends Tab ─────────────────────────────────────────────── */

interface FriendsTabProps {
  users: InvitableUser[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  selectedUserIds: Set<string>;
  onToggleUser: (id: string) => void;
  hasGroups: boolean;
}

const FriendsTab: React.FC<FriendsTabProps> = ({
  users,
  loading,
  error,
  searchQuery,
  onSearchChange,
  selectedUserIds,
  onToggleUser,
  hasGroups,
}) => {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: theme.spacing.lg, color: theme.colors.textSecondary }}>
        Cargando jugadores...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: theme.spacing.lg, color: theme.colors.danger }}>
        {error}
      </div>
    );
  }

  if (users.length === 0 && !searchQuery) {
    return (
      <div style={{ textAlign: 'center', padding: theme.spacing.lg }}>
        <p style={{ color: theme.colors.textSecondary, fontFamily: theme.fonts.body, margin: '0 0 12px 0' }}>
          {hasGroups
            ? 'Todos los miembros ya fueron invitados o estan en el partido.'
            : 'No tenes amigos o grupos para invitar todavia.'}
        </p>
        {!hasGroups && (
          <a
            href="/groups/create"
            style={{
              color: theme.colors.primary,
              fontFamily: theme.fonts.body,
              fontWeight: 700,
              textDecoration: 'none',
            }}
          >
            Crear un grupo
          </a>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Search */}
      <input
        type="text"
        placeholder="Buscar por nombre..."
        value={searchQuery}
        onChange={e => onSearchChange(e.target.value)}
        style={{
          width: '100%',
          padding: '10px 14px',
          backgroundColor: theme.colors.background,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: theme.borderRadius.input,
          color: theme.colors.text,
          fontFamily: theme.fonts.body,
          fontSize: theme.fonts.sizeSmall,
          outline: 'none',
          boxSizing: 'border-box',
          marginBottom: theme.spacing.sm,
        }}
      />

      {/* User list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {users.map(user => {
          const isDisabled = user.status === 'in_match' || user.status === 'invited';
          const isSelected = selectedUserIds.has(user.id);
          return (
            <div
              key={user.id}
              onClick={() => !isDisabled && onToggleUser(user.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.sm,
                padding: '10px 12px',
                borderRadius: '10px',
                cursor: isDisabled ? 'default' : 'pointer',
                opacity: isDisabled ? 0.5 : 1,
                backgroundColor: isSelected
                  ? 'rgba(0, 230, 118, 0.1)'
                  : 'transparent',
                border: isSelected
                  ? `1px solid ${theme.colors.primary}`
                  : '1px solid transparent',
                transition: 'all 0.15s ease',
              }}
            >
              {/* Checkbox */}
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '4px',
                  border: `2px solid ${isSelected ? theme.colors.primary : theme.colors.border}`,
                  backgroundColor: isSelected ? theme.colors.primary : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'all 0.15s ease',
                }}
              >
                {isSelected && (
                  <span style={{ color: '#000', fontSize: '12px', fontWeight: 700 }}>✓</span>
                )}
              </div>

              {/* Avatar */}
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: theme.colors.background,
                  border: `1px solid ${theme.colors.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  overflow: 'hidden',
                }}
              >
                {user.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <span style={{ color: theme.colors.textSecondary, fontSize: '14px', fontWeight: 700 }}>
                    {user.name.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    color: theme.colors.text,
                    fontFamily: theme.fonts.body,
                    fontSize: theme.fonts.sizeSmall,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user.name}
                </div>
                <div
                  style={{
                    color: theme.colors.textSecondary,
                    fontFamily: theme.fonts.body,
                    fontSize: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  {user.groupName && <span>{user.groupName}</span>}
                  {user.rating != null && user.rating > 0 && (
                    <span>{'★'.repeat(Math.round(user.rating))}</span>
                  )}
                </div>
              </div>

              {/* Status badge */}
              {isDisabled && (
                <span
                  style={{
                    fontSize: '0.7rem',
                    fontFamily: theme.fonts.body,
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '10px',
                    backgroundColor:
                      user.status === 'in_match'
                        ? 'rgba(0, 230, 118, 0.15)'
                        : 'rgba(255, 214, 0, 0.15)',
                    color:
                      user.status === 'in_match'
                        ? theme.colors.primary
                        : theme.colors.warning,
                    flexShrink: 0,
                  }}
                >
                  {user.status === 'in_match' ? 'ANOTADO' : 'INVITADO'}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {users.length > 0 && searchQuery && users.length === 0 && (
        <div style={{ textAlign: 'center', padding: theme.spacing.md, color: theme.colors.textSecondary }}>
          Sin resultados para "{searchQuery}"
        </div>
      )}
    </div>
  );
};

/* ── Share Tab ───────────────────────────────────────────────── */

interface ShareTabProps {
  shareUrl: string;
  matchTitle: string;
  copied: boolean;
  onCopyLink: () => void;
  onShareWhatsApp: () => void;
  onShareTelegram: () => void;
  isMobile: boolean;
}

const ShareTab: React.FC<ShareTabProps> = ({
  shareUrl,
  copied,
  onCopyLink,
  onShareWhatsApp,
  onShareTelegram,
  isMobile,
}) => {
  // On desktop: copy link is primary. On mobile: WhatsApp is primary.
  const buttons = isMobile
    ? [
        { label: 'Compartir en WhatsApp', icon: '💬', color: '#25D366', onClick: onShareWhatsApp },
        { label: 'Compartir en Telegram', icon: '✈️', color: '#0088cc', onClick: onShareTelegram },
        { label: copied ? 'Copiado!' : 'Copiar Link', icon: copied ? '✅' : '📋', color: copied ? theme.colors.success : theme.colors.secondary, onClick: onCopyLink },
      ]
    : [
        { label: copied ? 'Copiado!' : 'Copiar Link', icon: copied ? '✅' : '📋', color: copied ? theme.colors.success : theme.colors.primary, onClick: onCopyLink },
        { label: 'Compartir en WhatsApp', icon: '💬', color: '#25D366', onClick: onShareWhatsApp },
        { label: 'Compartir en Telegram', icon: '✈️', color: '#0088cc', onClick: onShareTelegram },
      ];

  return (
    <div>
      <p
        style={{
          color: theme.colors.text,
          fontFamily: theme.fonts.body,
          fontSize: theme.fonts.sizeSmall,
          margin: '0 0 16px 0',
        }}
      >
        Comparte este link para que otros jugadores se sumen:
      </p>

      {/* URL display */}
      <div
        style={{
          backgroundColor: theme.colors.background,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: theme.borderRadius.input,
          padding: theme.spacing.sm,
          marginBottom: theme.spacing.md,
          wordBreak: 'break-all',
        }}
      >
        <code
          style={{
            color: theme.colors.textSecondary,
            fontFamily: 'monospace',
            fontSize: '12px',
          }}
        >
          {shareUrl}
        </code>
      </div>

      {/* Share buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
        {buttons.map((btn, i) => (
          <button
            key={i}
            onClick={btn.onClick}
            style={{
              padding: theme.spacing.sm,
              backgroundColor: btn.color,
              color: theme.colors.white,
              border: 'none',
              borderRadius: theme.borderRadius.button,
              fontFamily: theme.fonts.body,
              fontSize: theme.fonts.sizeBody,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: theme.spacing.sm,
              transition: 'background-color 0.2s ease',
            }}
          >
            <span>{btn.icon}</span>
            <span>{btn.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

/* ── Guest Tab ───────────────────────────────────────────────── */

interface GuestTabProps {
  isOrganizer: boolean;
  guestName: string;
  guestPosition: string;
  guestRating: number;
  onNameChange: (v: string) => void;
  onPositionChange: (v: string) => void;
  onRatingChange: (v: number) => void;
  onSubmit: () => void;
  adding: boolean;
  pendingMembers?: GroupPendingMember[];
  onAddPendingMember?: (pm: GroupPendingMember) => void;
}

const GuestTab: React.FC<GuestTabProps> = ({
  isOrganizer,
  guestName,
  guestPosition,
  guestRating,
  onNameChange,
  onPositionChange,
  onRatingChange,
  onSubmit,
  adding,
  pendingMembers = [],
  onAddPendingMember,
}) => {
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 14px',
    backgroundColor: theme.colors.background,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.input,
    color: theme.colors.text,
    fontFamily: theme.fonts.body,
    fontSize: theme.fonts.sizeSmall,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    color: theme.colors.textSecondary,
    fontFamily: theme.fonts.body,
    fontSize: '0.75rem',
    fontWeight: 600,
    marginBottom: '4px',
    display: 'block',
  };

  return (
    <div>
      <p
        style={{
          color: theme.colors.textSecondary,
          fontFamily: theme.fonts.body,
          fontSize: theme.fonts.sizeSmall,
          margin: '0 0 16px 0',
        }}
      >
        {isOrganizer
          ? 'Agrega un jugador que no tiene cuenta en Futbolero.'
          : 'Propone un invitado al organizador.'}
      </p>

      {/* Pending members del grupo — quick-add (organizer only) */}
      {isOrganizer && pendingMembers.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <p style={{
            color: theme.colors.text,
            fontFamily: theme.fonts.body,
            fontSize: '0.8rem',
            fontWeight: 700,
            margin: '0 0 8px 0',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            Del grupo
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {pendingMembers.map(pm => (
              <div
                key={pm.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '10px',
                  padding: '10px 12px',
                  backgroundColor: theme.colors.background,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '10px',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    color: theme.colors.text,
                    fontFamily: theme.fonts.body,
                    fontSize: theme.fonts.sizeSmall,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {pm.guestName}
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '2px',
                  }}>
                    <span style={{ color: theme.colors.textSecondary, fontFamily: theme.fonts.body, fontSize: '0.7rem' }}>
                      {pm.guestPosition}
                    </span>
                    <span style={{ color: '#FFD700', fontSize: '0.7rem' }}>
                      {'★'.repeat(pm.guestRating)}{'☆'.repeat(5 - pm.guestRating)}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => onAddPendingMember?.(pm)}
                  style={{
                    padding: '6px 14px',
                    backgroundColor: theme.colors.primary,
                    color: '#000',
                    border: 'none',
                    borderRadius: '8px',
                    fontFamily: theme.fonts.body,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  Agregar
                </button>
              </div>
            ))}
          </div>
          <div style={{
            borderTop: `1px solid ${theme.colors.border}`,
            margin: '16px 0',
          }} />
          <p style={{
            color: theme.colors.textSecondary,
            fontFamily: theme.fonts.body,
            fontSize: '0.75rem',
            fontWeight: 600,
            margin: '0 0 10px 0',
          }}>
            O ingresá un jugador nuevo:
          </p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
        {/* Name (required) */}
        <div>
          <label style={labelStyle}>Nombre *</label>
          <input
            type="text"
            placeholder="Ej: El Flaco"
            value={guestName}
            onChange={e => onNameChange(e.target.value)}
            style={inputStyle}
          />
        </div>

        {/* Position (optional) */}
        <div>
          <label style={labelStyle}>Posicion (opcional)</label>
          <select
            value={guestPosition}
            onChange={e => onPositionChange(e.target.value)}
            style={{ ...inputStyle, appearance: 'auto' }}
          >
            <option value="">Sin especificar</option>
            {POSITIONS.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>

        {/* Rating (optional) */}
        <div>
          <label style={labelStyle}>Nivel (opcional)</label>
          <div style={{ display: 'flex', gap: '4px' }}>
            {[1, 2, 3, 4, 5].map(star => (
              <button
                key={star}
                onClick={() => onRatingChange(guestRating === star ? 0 : star)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '24px',
                  color: star <= guestRating ? theme.colors.warning : theme.colors.border,
                  padding: '2px',
                  transition: 'color 0.15s ease',
                }}
              >
                ★
              </button>
            ))}
          </div>
        </div>

        {/* Submit */}
        <button
          onClick={onSubmit}
          disabled={!guestName.trim() || adding}
          style={{
            width: '100%',
            padding: theme.spacing.sm,
            backgroundColor: !guestName.trim() || adding ? theme.colors.secondary : theme.colors.primary,
            color: !guestName.trim() || adding ? theme.colors.textSecondary : '#000',
            border: 'none',
            borderRadius: theme.borderRadius.button,
            fontFamily: theme.fonts.body,
            fontSize: theme.fonts.sizeBody,
            fontWeight: 700,
            cursor: !guestName.trim() || adding ? 'not-allowed' : 'pointer',
            marginTop: theme.spacing.xs,
            transition: 'all 0.2s ease',
          }}
        >
          {adding
            ? 'Agregando...'
            : isOrganizer
              ? 'Agregar invitado'
              : 'Proponer invitado'}
        </button>
      </div>
    </div>
  );
};
